import { App as AppCap } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import {
  IonAccordion,
  IonAccordionGroup,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonModal,
  IonNote,
  IonText,
  IonTitle,
  IonToolbar,
  IonSegment,
  IonSegmentButton,
} from "@ionic/react";
import {
  arrowForwardOutline,
  close,
  person,
  playOutline,
} from "ionicons/icons";
import React, { useContext, useEffect, useState } from "react";
import { TOOLS_LINKS } from "./Constants";
import UserPrefencesContext, {
  UserPreferences,
} from "../components/contexts/UserPreferencesContext";
import LogoutButton from "../components/helpers/LogoutButton";
import config_common from "../platforms/cmed/common/utils/config_common";
import { pubsubEvents } from "../components/helpers/socket/pubsubEvent";
import CMELogoForDarkTheme from "../img/cmelogo_for_dark_theme.png";
import CMELogoForLightTheme from "../img/cmelogo_for_light_theme.png";
import { hapticsImpactMedium } from "../utils/core-utils/hapticUtils";
import { saveUserPreferences } from "../utils/core-utils/preferenceUtils";
import CMEDProfilesPage from "./CMEDProfilesPage";
import { AppThemes } from "../components/Constants";

type UserSettingsModalProps = {
  isUserSettingsModalOpen: any;
  setIsUserSettingsModalOpen: any;
  loggedInUserDetails: any;
  entitlements: any;
  isCMEDProfileSelectionCompleted: any;
};

const tour_list_cmed_mobile = [
  { name: "Market", value: "market" },
  { name: "Orders", value: "order" },
  { name: "Block", value: "blockFeed" },
  // { name: "RFQ Feed", value: "rfqFeed"}, Temporarily removing rfq feed from app tour
  { name: "Options", value: "optionsGrid" },
];

const isWebMode = Capacitor.getPlatform().includes("web");

const UserSettingsModal: React.FC<UserSettingsModalProps> = ({
  isUserSettingsModalOpen,
  setIsUserSettingsModalOpen,
  entitlements,
}) => {
  const themeValue = useContext(
    UserPrefencesContext,
  ).userPreferences.theme.replace(/"/g, "");

  const [fullName, setfullName] = useState(undefined);
  const [CMEDMobileappTourList, setCMEDMobileappTourList] = useState(
    tour_list_cmed_mobile,
  );

  const { userPreferences, setUserPreferences } =
    useContext(UserPrefencesContext);

  useEffect(() => {
    setTheme(themeValue);
  }, [themeValue]);

  useEffect(() => {
    PubSub.subscribe(
      pubsubEvents.socketKeys.GET_FULL_NAME_SUCCESS,
      (msg, data) => {
        setfullName(data.payload);
      },
    );
  }, []);

  const [theme, setTheme] = useState(AppThemes.DARK);
  const [renderDarkThemeLogo, setRenderDarkThemeLogo] = useState(false);
  const [isCMEDLoggedIn, setisCMEDLoggedIn] = useState(false);

  const [isOpenProfileModal, setIsOpenProfileModal] = useState(false);

  const [appVersion, setAppVersion] = useState<string | undefined>(undefined);

  useEffect(() => {
    const fetchAppVersion = async () => {
      const version = await getAppInfo();
      setAppVersion(version);
    };

    !isWebMode && fetchAppVersion();
  }, []);

  async function getAppInfo() {
    const info = await AppCap.getInfo();
    console.log(info);
    if (Capacitor.getPlatform() === "ios") {
      return `${info.version}.${info.build}`;
    } else {
      return info.version;
    }
  }

  // Check/uncheck the toggle and update the theme based on isDark
  const initializeDarkTheme = (isDark: boolean) => {
    document.body.classList.toggle(AppThemes.DARK, isDark);
    if (isDark) {
      setRenderDarkThemeLogo(true);
    } else {
      setRenderDarkThemeLogo(false);
    }
  };

  useEffect(() => {
    if (userPreferences.app_tour !== "") {
      if (typeof userPreferences.app_tour == "string") {
        let tourData = JSON.parse(userPreferences.app_tour);
        // let appTourData = CMEDMobileappTourList.map((item: any) => {
        //   item.checked = !tourData[item.value];
        //   return item;
        // });
        let appTourData = [{ name: "Market", value: "market" }];
        setCMEDMobileappTourList(appTourData);
      }
    }
  }, [JSON.stringify(userPreferences.app_tour)]);

  useEffect(() => {
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)");
    if (theme === AppThemes.DARK) {
      initializeDarkTheme(true);
    }
    if (theme === AppThemes.LIGHT) {
      initializeDarkTheme(false);
    }
    if (theme === AppThemes.DEFAULT) {
      initializeDarkTheme(prefersDark.matches);
      setRenderDarkThemeLogo(prefersDark.matches);
    }
  }, [theme]);

  useEffect(() => {
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)");

    prefersDark.addEventListener("change", (mediaQuery) => {
      if (theme === AppThemes.DARK) {
        initializeDarkTheme(true);
      }
      if (theme === AppThemes.LIGHT) {
        initializeDarkTheme(false);
      }
      if (theme === AppThemes.DEFAULT) {
        initializeDarkTheme(mediaQuery.matches);
        setRenderDarkThemeLogo(mediaQuery.matches);
      }
    });
  }, [theme]);

  var getLogonResponse = function (msg: any, data: any) {
    setisCMEDLoggedIn(data.payload.isLoggedIn);
  };

  useEffect(() => {
    PubSub.subscribe(
      pubsubEvents.stompSubscriptionKeys.CMED_USER_LOGON_SUCCESS,
      getLogonResponse,
    );
  }, []);

  const setFontSize = (size: string) => {
    document.body.classList.remove("font-small", "font-medium", "font-large");
    document.body.classList.add(size);
  };

  const handleFontSizeChange = (e: any) => {
    const newSize = e.detail.value; // This will be 'SMALL', 'MEDIUM', or 'LARGE'

    const updatedPreferences: UserPreferences = {
      ...userPreferences,
      fontSize: newSize,
    };
    setUserPreferences(updatedPreferences);

    // Persist the preference
    saveUserPreferences(updatedPreferences);

    // Apply the font size class to body
    switch (newSize) {
      case "SMALL":
        setFontSize(config_common.fontSizes.SMALL);
        break;
      case "MEDIUM":
        setFontSize(config_common.fontSizes.MEDIUM);
        break;
      case "LARGE":
        setFontSize(config_common.fontSizes.LARGE);
        break;
      default:
        setFontSize(config_common.fontSizes.MEDIUM);
    }
  };

  // triggering driver js animation

  const triggerDriver = (data: any) => {
    userPreferences.app_tour = JSON.stringify(data);
    setUserPreferences(userPreferences);
    saveUserPreferences(userPreferences);
  };

  const updatingUserPref = (type: string, checked: boolean) => {
    const newUserPref = Object.assign({}, JSON.parse(userPreferences.app_tour));
    newUserPref[type] = checked;
    return newUserPref;
  };

  const onTriggerDriver = (isChecked: any, type: any) => {
    switch (type) {
      case "Market":
        triggerDriver(updatingUserPref("market", isChecked));
        if (!isChecked) {
          window.document.getElementById("tab-button-Market")!.click();
          setIsUserSettingsModalOpen(false);
        }
        break;

      case "Orders":
        triggerDriver(updatingUserPref("order", isChecked));
        if (!isChecked) {
          window.document.getElementById("tab-button-Orders")!.click();
          setIsUserSettingsModalOpen(false);
        }
        break;

      case "RFQ Feed":
        triggerDriver(updatingUserPref("rfqFeed", isChecked));
        if (!isChecked) {
          window.document.getElementById("tab-button-RFQFeed")!.click();
          setIsUserSettingsModalOpen(false);
        }
        break;

      case "Block":
        triggerDriver(updatingUserPref("blockFeed", isChecked));
        if (!isChecked) {
          window.document.getElementById("tab-button-BlockFeed")!.click();
          setIsUserSettingsModalOpen(false);
        }
        break;

      case "Options":
        triggerDriver(updatingUserPref("optionsGrid", isChecked));
        if (!isChecked) {
          window.document.getElementById("tab-button-OptionsGrid")!.click();
          setIsUserSettingsModalOpen(false);
        }
        break;
    }
  };

  function toTitleCase(str: any) {
    return str.replace(/\w\S*/g, function (txt: any) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }

  return (
    <>
      <IonModal
        isOpen={isOpenProfileModal}
        onDidDismiss={() => {
          setIsOpenProfileModal(false);
        }}
        canDismiss={true}
        initialBreakpoint={0.9}
        breakpoints={[0, 0.9, 1]}
      >
        <div className="ion-padding modal_header">
          <h5 style={{ margin: 0, padding: 0 }}>CMED Profiles</h5>
          <IonIcon
            icon={close}
            style={{ fontSize: "20px" }}
            // style={{ fontSize: "var(--one-font-size-8)" }}
            onClick={() => {
              setIsOpenProfileModal(false);
              hapticsImpactMedium();
            }}
          ></IonIcon>
        </div>
        <div className="divider"></div>
        <IonContent>
          <CMEDProfilesPage />
        </IonContent>
      </IonModal>
      <IonModal
        isOpen={isUserSettingsModalOpen}
        onDidDismiss={() => setIsUserSettingsModalOpen(false)}
        canDismiss={true}
        initialBreakpoint={1}
        breakpoints={[0, 1]}
      >
        <IonHeader className="ion-no-border">
          <IonToolbar>
            <IonTitle>USER SETTINGS</IonTitle>
            <IonButtons slot="end">
              <IonButton
                onClick={() => {
                  setIsUserSettingsModalOpen(false);
                }}
              >
                Close
              </IonButton>
            </IonButtons>
          </IonToolbar>
          <IonList inset={true}>
            {entitlements?.includes("CMED") ? (
              <IonItem
                button
                lines="none"
                color="light"
                onClick={() => {
                  setIsOpenProfileModal(true);
                  hapticsImpactMedium();
                }}
                detail
              >
                <IonIcon icon={person} slot="start"></IonIcon>
                <IonLabel>
                  <IonLabel>
                    <h2 className="ion-text-wrap">
                      {fullName && toTitleCase(fullName)}
                    </h2>
                  </IonLabel>
                  <IonLabel>
                    <p>Tap here to see your CMED Profiles/Accounts</p>
                  </IonLabel>
                </IonLabel>
              </IonItem>
            ) : (
              <IonItem lines="none" color="light">
                <IonIcon icon={person} slot="start"></IonIcon>
                <IonLabel>
                  <h2 className="ion-text-wrap">
                    {fullName && toTitleCase(fullName)}
                  </h2>
                </IonLabel>
              </IonItem>
            )}
          </IonList>
        </IonHeader>
        <IonContent>
          <IonListHeader>Font Size</IonListHeader>
          <IonList className="ion-no-padding" inset={true}>
            <IonSegment
              value={userPreferences.fontSize}
              className="tab_based_segment"
              onIonChange={handleFontSizeChange}
            >
              <IonSegmentButton
                value="SMALL"
                className="tab_based_segment_button"
              >
                <IonText
                  style={{
                    fontSize: "var(--one-font-size-7-small)",
                    "font-weight": "bold",
                  }}
                >
                  Small
                </IonText>
              </IonSegmentButton>
              <IonSegmentButton
                value="MEDIUM"
                className="tab_based_segment_button"
              >
                <IonText
                  style={{
                    fontSize: "var(--one-font-size-7-medium)",
                    "font-weight": "bold",
                  }}
                >
                  Medium
                </IonText>
              </IonSegmentButton>
              <IonSegmentButton
                value="LARGE"
                className="tab_based_segment_button"
              >
                <IonText
                  style={{
                    fontSize: "var(--one-font-size-7-large)",
                    "font-weight": "bold",
                  }}
                >
                  Large
                </IonText>
              </IonSegmentButton>
            </IonSegment>
          </IonList>
          <IonListHeader>CME Direct Tour</IonListHeader>
          <IonAccordionGroup expand="inset">
            <IonAccordion>
              <IonItem slot="header" color="light">
                <IonIcon icon={playOutline} slot="start"></IonIcon>
                <IonLabel>
                  <h2>Start CME Direct tour</h2>
                  <IonNote color="medium" className="ion-text-wrap">
                    Take a demo tour for the refreshed CME Direct!
                  </IonNote>
                </IonLabel>
              </IonItem>
              <div slot="content">
                {CMEDMobileappTourList.map((item, index) => (
                  <IonItem
                    key={index}
                    className={index % 2 === 0 ? "even_item_bg" : ""}
                    lines="none"
                    disabled={isCMEDLoggedIn === false}
                    onClick={(e) => onTriggerDriver(false, item.name)}
                  >
                    {item.name}
                    <IonIcon slot="end" icon={arrowForwardOutline}></IonIcon>
                  </IonItem>
                ))}
              </div>
            </IonAccordion>
          </IonAccordionGroup>
        </IonContent>
        <IonFooter className="ion-padding">
          {renderDarkThemeLogo ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <img src={CMELogoForDarkTheme} height={60} />
              {!isWebMode && appVersion && (
                <p style={{ fontSize: "13px" }}>Version: {appVersion}</p>
              )}
              <IonText color="dark">
                <p style={{ fontSize: "12px" }}>
                  &copy; {new Date().getFullYear()} CME Group Inc.
                </p>
              </IonText>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <img src={CMELogoForLightTheme} height={60} />
              {!isWebMode && appVersion && (
                <p style={{ fontSize: "var(--one-font-size-9)" }}>
                  Version: {appVersion}
                </p>
              )}
              <IonText color="dark">
                <p style={{ fontSize: "var(--one-font-size-8)" }}>
                  &copy; {new Date().getFullYear()} CME Group Inc.
                </p>
              </IonText>
            </div>
          )}
          <LogoutButton />
        </IonFooter>
      </IonModal>
    </>
  );
};

export default UserSettingsModal;
